import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, ArrowRight, Calendar, Truck, MapPin } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { SectionTitle } from "@/components/SectionTitle";
import { Button } from "@/components/ui/button";
import CartDrawer from "@/components/CartDrawer";
import { useSite } from "@/context/SiteContext";
import { useCart } from "@/context/CartContext";
import { api, fileUrl, brl } from "@/lib/api";

export default function StorePage() {
  const { settings } = useSite();
  const { count } = useCart();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const campaignOpen = settings?.campaign_open;

  useEffect(() => {
    document.title = `${settings?.campaign_title || "Pagode das Cinco Pontas"} | Loja Oficial`;
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, [settings]);

  const mainProducts = products.filter((p) => !p.is_cap);
  const caps = products.filter((p) => p.is_cap);

  return (
    <div className="App">
      <Header />

      {/* Store top bar with cart */}
      <div className="border-b border-[var(--border)] bg-[var(--bg-secondary)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <span className="text-sm font-semibold text-[var(--text-muted)]">Loja Oficial do Evento</span>
          {campaignOpen && (
            <button onClick={() => setCartOpen(true)} data-testid="open-cart-button"
              className="relative inline-flex items-center gap-2 px-4 py-2 rounded-full btn-primary font-bold text-sm">
              <ShoppingCart size={16} /> Carrinho
              {count > 0 && (
                <span className="absolute -top-2 -right-2 bg-[var(--accent-2)] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{count}</span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={fileUrl(settings?.store_hero_image)} alt="" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-primary)] via-[var(--bg-primary)]/60 to-[var(--bg-primary)]/15" />
          <div className="absolute inset-0 opacity-20 accent-bar mix-blend-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            {!campaignOpen && (
              <span data-testid="campaign-ended-badge" className="inline-block mb-4 px-4 py-1.5 rounded-full text-sm font-bold" style={{ background: "#2D1216", color: "#EF4444", border: "1px solid #EF4444" }}>
                Vendas encerradas
              </span>
            )}
            <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              {settings?.campaign_title || "Pagode das Cinco Pontas"}
            </h1>
            <p className="text-[var(--text-muted)] text-lg mt-5 max-w-2xl mx-auto">
              Edição limitada. {campaignOpen ? "Compre online com Pix ou cartão e escolha entrega ou retirada no evento." : "As vendas deste evento foram encerradas."}
            </p>
            {campaignOpen && (
              <a href="#produtos"><Button className="btn-primary font-bold h-12 px-8 mt-7">Ver produtos <ArrowRight className="ml-2" size={18} /></Button></a>
            )}
          </motion.div>
        </div>
      </section>

      {/* ENDED NOTICE */}
      {!campaignOpen && (
        <section className="max-w-3xl mx-auto px-4 py-12">
          <div className="rounded-2xl p-8 text-center" style={{ background: "#2D1216", border: "1px solid #EF4444" }} data-testid="campaign-ended-notice">
            <h2 className="font-head text-2xl font-bold text-white">Vendas encerradas</h2>
            <p className="text-[var(--text-muted)] mt-3">
              A campanha do evento foi encerrada e não está mais recebendo novos pedidos.
              {settings?.whatsapp_number ? " Para dúvidas sobre pedidos já realizados, fale conosco pelo WhatsApp." : ""}
            </p>
            {settings?.contact_email && (
              <a href={`mailto:${settings.contact_email}`} className="inline-block mt-4 text-[var(--accent)] font-semibold">{settings.contact_email}</a>
            )}
          </div>
        </section>
      )}

      {/* PRODUCTS */}
      <section id="produtos" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {products.length === 0 ? (
          <div className="card-surface p-10 text-center text-[var(--text-muted)]" data-testid="store-empty">
            Os produtos serão exibidos assim que cadastrados no painel.
          </div>
        ) : (
          <>
            <SectionTitle eyebrow="Produtos oficiais" title="Escolha o seu modelo" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mainProducts.map((p) => (
                <ProductCard key={p.id} p={p} campaignOpen={campaignOpen} navigate={navigate} />
              ))}
            </div>

            {caps.length > 0 && (
              <div className="mt-16">
                <div className="rounded-2xl p-6 mb-6 border border-[var(--border)] bg-[var(--bg-card)]">
                  <h3 className="font-head font-bold text-xl">Complemente com um boné</h3>
                  <p className="text-[var(--text-muted)] text-sm mt-1">Item opcional — adicione apenas se quiser.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {caps.map((p) => (
                    <ProductCard key={p.id} p={p} campaignOpen={campaignOpen} navigate={navigate} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* DELIVERY INFO */}
      {(settings?.store_delivery_info || settings?.store_pickup_info) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-2 gap-6">
          {settings.store_pickup_info && (
            <div className="card-surface p-7"><MapPin className="text-[var(--accent)] mb-3" /><h3 className="font-head font-bold text-lg">Retirada no evento</h3><p className="text-[var(--text-muted)] text-sm mt-2 whitespace-pre-line">{settings.store_pickup_info}</p></div>
          )}
          {settings.store_delivery_info && (
            <div className="card-surface p-7"><Truck className="text-[var(--accent)] mb-3" /><h3 className="font-head font-bold text-lg">Entrega</h3><p className="text-[var(--text-muted)] text-sm mt-2 whitespace-pre-line">{settings.store_delivery_info}</p></div>
          )}
        </section>
      )}

      <Footer brand="stamp" />
      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
    </div>
  );
}

function ProductCard({ p, campaignOpen, navigate }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className="card-surface overflow-hidden flex flex-col" data-testid={`product-card-${p.id}`}>
      <Link to={`/pagode-das-cinco-pontas/produto/${p.id}`} className="block relative aspect-square overflow-hidden bg-[var(--bg-secondary)]">
        {p.photos?.[0] ? (
          <img src={fileUrl(p.photos[0])} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[var(--text-muted)]">Sem foto</div>
        )}
        {p.is_cap && <span className="absolute top-3 left-3 text-[10px] font-bold uppercase bg-[var(--accent)] text-black px-2 py-1 rounded">Opcional</span>}
      </Link>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-head font-bold text-lg">{p.name}</h3>
        {p.description && <p className="text-[var(--text-muted)] text-sm mt-1 line-clamp-2">{p.description}</p>}
        <div className="mt-3 text-2xl font-black text-[var(--accent)]">{brl(p.price)}</div>
        <Button onClick={() => navigate(`/pagode-das-cinco-pontas/produto/${p.id}`)} disabled={!campaignOpen}
          data-testid={`product-view-${p.id}`} className="mt-4 btn-primary font-bold w-full disabled:opacity-40">
          {campaignOpen ? "Escolher tamanho" : "Vendas encerradas"}
        </Button>
      </div>
    </motion.div>
  );
}
